import '../../@types';

import { createElementByHtmlString } from './loader';

import dialogLayerHtml from './dialog-layer.html';
import { isInWujie } from './env';

/** PHP explode 的 JavaScript 实现：使用一个字符串分割另一个字符串 */
export function explode(sep: string, str: string, limit?: number): string[] {
    if (!str) return [];
    const arr = str.split(sep);
    if (!limit || limit <= 0 || arr.length <= limit) return arr;
    const tmp = arr.splice(0, limit - 1);
    tmp.push(arr.join(sep));
    return tmp;
}

/** 传入的值是否全为 null 或 undefined 或 空字符串 */
export function isEmpty(...vals: any): boolean {
    let ok = vals.length < 1; // 不传参为 true，其它情况初始化为 false
    for (const v of vals) {
        ok = v == null || v === '';
        if (!ok) break;
    }
    console.debug('[Act][isEmpty]', ok, vals);
    return ok;
}

/** 将后面的对象合并到 obj */
export function objectMerge(obj: any, ...objs: any[]) {
    for (const o of objs) {
        for (const [k, v] of Object.entries(o)) {
            if (v == null || v === '') continue;
            obj[k] = v;
        }
    }
    return obj;
}

export function requireParams(obj: any, ...names: any[]): boolean {
    if (!obj) return false;
    let ok = true;
    for (const n of names) {
        if (obj[n] == null || obj[n] === '') {
            ok = false;
            console.warn(`[Act] requireParams: ${n} 为空`, obj);
            break;
        }
    }
    return ok;
}

/** 从指定链接中获取全部参数值 */
export function getAllQueryParams(url?: string): { [k: string]: string } {
    const out: { [k: string]: string } = {};
    if (!url) url = (window.$wujie?.location ?? location).search;
    if (!url) return out;
    const [, queryString] = explode('?', url, 2);
    if (!queryString) return out;
    const arr = queryString.split('&');
    for (const s of arr) {
        let [k, v] = explode('=', s, 2);
        if (v == null) continue;
        k = decodeURIComponent(k);
        v = decodeURIComponent(v);
        out[k] = v;
    }
    return out;
}

/** 获取指定的 URL 链接中获取参数，参数名可以是一个字符串，也可以是一个字符串数组，如果是字符串数组，它表示该参数有多个别名，则会按顺序逐一尝试获取直到获取到非空值（undefined/null) */
export function getQueryParamsFromUrl(url: string, ...paramNames: (string | string[])[]) {
    const out: { [k: string]: string } = {};
    for (let names of paramNames) {
        if (typeof names === 'string') names = [names];
        else names = Array.from(new Set(names));
        for (const n of names) {
            const reg = new RegExp(`\\b${encodeURIComponent(n)}(?:=([^&#?]*))`);
            const matched = url.match(reg);
            let v = matched?.[1];
            if (v == null) continue;
            v = decodeURIComponent(v);
            for (const n of names) out[n] = v;
            break;
        }
    }
    return out;
}

/** 从当前页面链接中获取参数，多个 name 表示该参数有多个不同的别名 */
export function getQueryParam(...names: string[]): string | undefined {
    const params = getQueryParamsFromUrl(window.$wujie?.location.search ?? location.search, names);
    for (const name of names) {
        if (params[name] != null) return params[name];
    }
    return undefined;
}

/** 获取当前链接中获取多个参数值，多个 name 表示不同的参数名 */
export function getQueryParams(...names: (string | string[])[]) {
    return getQueryParamsFromUrl(window.$wujie?.location.search ?? location.search, ...names);
}

/** 构造链接参数 */
export function buildQueryParams(params: { [k: string]: string | number | boolean | null | undefined }): string {
    const arr: string[] = [];
    for (const [k, v] of Object.entries(params)) {
        if (v == null) continue;
        arr.push(k + '=' + encodeURIComponent(v));
    }
    return arr.join('&');
}

/** 解码 JSON 字符串，失败时不抛出异常，返回 undefined */
export function json_decode(str: string): any {
    if (!str) return undefined;
    try {
        return new Function(`return (${str});`)();
    } catch (e) {
        console.error('[Act] json_decode exception:', e);
        return undefined;
    }
}

export function decodeUnicode(str: string): string {
    try {
        return JSON.parse(`"${str}"`);
    } catch (err) {
        console.error(`[Act] decodeUnicode(${str}) exception:`, err);
    }
    return str;
}

export function prefixNumber(num: number, prefix = '0'): string {
    if (num > 9) return String(num);
    return prefix + num;
}

export function formatDate(format: string, date?: Date | number): string {
    if (!date) date = new Date();
    else if (!(date instanceof Date)) date = new Date(date);
    const arr: (string | number)[] = [];
    for (let i = 0; i < format.length; i++) {
        const s = format[i];
        if (s === 'Y') arr.push(date.getFullYear());
        else if (s === 'm') arr.push(prefixNumber(date.getMonth() + 1));
        else if (s === 'd') arr.push(prefixNumber(date.getDate()));
        else if (s === 'H') arr.push(prefixNumber(date.getHours()));
        else if (s === 'i') arr.push(prefixNumber(date.getMinutes()));
        else if (s === 's') arr.push(prefixNumber(date.getSeconds()));
        else arr.push(s);
    }
    return arr.join('');
}

export function isMobile() {
    // 常见的移动浏览器User-Agent中的关键词
    const reg = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Opera Mobi|Windows Phone/i;
    // @ts-ignore 获取用户代理字符串
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return reg.test(userAgent);
}

/** 判断当前 UA 是否是 iOS */
export function isIOS(): boolean {
    return /\biPhone|\biPad|iPod|\bMac/i.test(navigator.userAgent);
}

/** 是否是三星 */
export function isSamsung(): boolean {
    return /SAMSUNG|SGH-[I|N|T]|GT-[I|P|N]|SM-[N|P|T|Z|G]|SHV-E|SCH-[I|J|R|S]|SPH-L/i.test(navigator.userAgent);
}

/** 是否是 Instagram */
export function isInstagram(): boolean {
    return /\bInstagram/.test(navigator.userAgent);
}

/** 是否是 Facebook */
export function isFacebook(): boolean {
    return /\bFBAN|\bFBAV|\bFBIOS/.test(navigator.userAgent);
}

/**
 * 是否在WeChat APP中
 */
export function isWeChat () {
    return /MicroMessenger/gi.test(navigator.userAgent);
}

/**
 * 是否在QQ APP中
 */
export function isQQ(){
    const REGEXP_IOS_QQ = new RegExp("(iPad|iPhone|iPod).*? (IPad)?QQ\\/([\\d\\.]+)");
    const REGEXP_ANDROID_QQ = new RegExp("\\bV1_AND_SQI?_([\\d\\.]+)(.*? QQ\\/([\\d\\.]+))?", "ig");
    //判断是否是 IOSQQ 或者 AndroidQQ打开
    const isIphoneOs_QQ = REGEXP_IOS_QQ.test(navigator.userAgent);
    const isAndroid_QQ = REGEXP_ANDROID_QQ.test(navigator.userAgent);
    return isIphoneOs_QQ || isAndroid_QQ;
}


declare interface INTLUserInfo {
    [k: string]: string;
    sOpenId: string;
    sopenid: string;
    sRoleId: string;
    sArea: string;
    os: string;
    sPartition: string;
    channelid: string;
    encodeparam: string;
}

let isGame: boolean;

/** 判断是否在游戏内浏览器打开 */
export function getINTLUserInfo(): INTLUserInfo | undefined {
    const params = getAllQueryParams((isInWujie() ? parent.location : location).search || (isInWujie() ? parent.location : location).hash);
    // if (!requireParams(params, 'encodeparam', 'gameid', 'os', 'channelid')) return undefined;
    if (!params.sOpenId) params.sOpenId = params.sopenId;
    if (!params.sArea && params.area != null) {
        const [plat, area] = params.area.split(',');
        params.sArea = area || plat;
    }
    if (!params.sPlatId) params.sPlatId = params.os;
    if (!params.sPartition) params.sPartition = params.partition;
    return params as any;
}

/** 通过 userAgent 判断是否是 INTL 浏览器 */
export function isINTLBrowser(): boolean {
    // if (isGame != null) return isGame;
    // isGame = getINTLUserInfo() != null;
    // return isGame;
    return /INTLBrowser/i.test(navigator.userAgent);
}

/** 检查是否包含汉字 */
export function testHan(str: string): boolean {
    return /[\u4e00-\u9fa5]/.test(str);
}

/** 检测是否全部都是单字节字符 */
export function testAllBytes(str: string): boolean {
    return /^[^\u0100-\uffff]*$/.test(str);
}

export function regisGlobalVariable(vars: { [k: string]: any }) {
    for (const [k, v] of Object.entries(vars)) {
        window[k] = v;
        if (isInWujie()) parent[k] = v;
    }
}

export const showDialog = {
    show(id: string) {
        let layer = document.getElementById('dialog-layer-wrap') as HTMLDivElement;
        if (!layer) {
            const fragment = createElementByHtmlString(dialogLayerHtml);
            document.body.append(fragment);
            layer = document.getElementById('dialog-layer-wrap') as HTMLDivElement;
        }
        layer.style.display = 'none';
        const dialog = document.getElementById(id) as HTMLDivElement;
        if (!dialog) return;
        $(layer).children().hide();
        if (dialog.parentElement !== layer) layer.append(dialog);
        dialog.style.display = 'block';
        layer.style.display = 'flex';
    },
    hide() {
        let layer = document.getElementById('dialog-layer-wrap') as HTMLDivElement;
        if (!layer) return;
        layer.style.display = 'none';
    },
};

let showTipsTimer: any;

/** 显示 Toast 提示，并在延迟 closeDelay 毫秒后自动关闭 */
export async function showTips(msg: string, closeDelay: number = 2000) {
    const layerId = 'toast-tips-layer';
    let layer = document.getElementById(layerId) as HTMLDivElement;
    if (!layer) {
        const fragment = createElementByHtmlString(`<div id=${layerId}>
    <div style="padding:10px 20px; text-align:center; background-color:#000c; color:#fff; font-size:18px; border-radius:10px; min-width:10%; max-width:90%"></div>
</div>`);
        document.body.append(fragment);
        layer = document.getElementById(layerId) as HTMLDivElement;
        Object.assign(layer.style, {
            position: 'fixed',
            zIndex: '20000',
            left: '0',
            top: '0',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            // pointerEvents: 'none',
        } as CSSStyleDeclaration);
    }
    layer.children[0].innerHTML = decodeUnicode(msg);
    layer.style.display = 'flex';

    if (showTipsTimer) clearTimeout(showTipsTimer);
    return new Promise((resolve) => {
        showTipsTimer = setTimeout(() => {
            showTipsTimer = undefined;
            layer.style.display = 'none';
            resolve(void 0);
        }, closeDelay);
    });
}

declare type HistoryState = { __type: 'HOK-ACT'; name: string | undefined; prev: string | undefined; data?: any };
declare type HistoryAction = (data?: any | undefined | undefined, prevName?: string) => any;
const historyRegistry: { [name: string]: HistoryAction } = {};

export function registerHistoryState(name: string | undefined, action: HistoryAction) {
    historyRegistry[name ?? '__INIT__'] = action;
}

export async function gotoState(name: string | undefined, data: any = undefined) {
    const action = historyRegistry[name ?? '__INIT__'];
    if (!action) return;
    const prev = parent.history.state?.name;
    await action(data, prev);
    const state: HistoryState = { __type: 'HOK-ACT', name, data, prev };
    if (name !== prev) {
        console.debug('[Act] push history state:', state);
        parent.history.pushState(state, '');
    } else {
        console.debug('[Act] replace history state:', state);
        parent.history.replaceState(state, '');
    }
}

parent.window.addEventListener('popstate', async (e) => {
    console.log('[Act] on popstate', e.state);
    const state: HistoryState = e.state;
    const { __type, name, data, prev } = state ?? {};
    // if (e.state != null && __type !== 'HOK-ACT') return;
    const action: HistoryAction = historyRegistry[name ?? '__INIT__'];
    return await action?.(data, name);
});

// window.addEventListener('load', () => {
//     const state: HistoryState = parent.history.state;
//     if (!state || state.__type !== 'HOK-ACT') return;
//     const { name, data, prev } = state;
//     console.log('[Act] 初始到导航界面', state);
//     historyRegistry[name ?? '__INIT__']?.(data, prev);
// });

regisGlobalVariable({ showDialog, showTips });



/**
 * 判断一个对象是否为空对象
 * @param obj
 */
export function isNonEmptyObject(obj:any):boolean {
    return obj && typeof obj === 'object' && Object.keys(obj).length > 0;
}

/**
 * 获取潘多拉传过来的数据解析
 * @param shareKey
 * @param defaultData
 */
export function getPandoraData<T extends {[key:string]:string}>(shareKey:string,defaultData:T):T{
    let tempData = getQueryParam(shareKey);
    if(!tempData){
        return defaultData;
    }
    tempData = decodeURIComponent(tempData);
    tempData = atob(tempData);
    let pandoraData = json_decode(tempData) as T;
    if(!pandoraData || !isNonEmptyObject(pandoraData)){
        pandoraData = defaultData;
    }
    
    // 第二步：遍历对象，对每个值进行 URL 解码
    const decodedObject = Object.keys(pandoraData).reduce((acc, key) => {
        const value = pandoraData[key] as string;
        try {
            // @ts-ignore
            acc[key] = decodeURIComponent(value);
        } catch (error) {
            if (error instanceof URIError) {
                console.error(`URL 解码失败（键: ${key}）:`, error.message);
                // @ts-ignore
                acc[key] = value;
            } else {
                throw error; // 如果不是 URIError，则重新抛出异常
            }
        }
        return acc;
    }, {} as Partial<T>); // 使用 Partial<T> 来初始化累加器对象
    const result = decodedObject as T;
    return result;
}



/**
 * 生成当前Url
 * 会获取之前的请求
 * @param prefix
 * @param data 需要添加的参数 会替换掉之前的重名参数
 */
export function getCurrentUrl(prefix: string = '', data: { [key: string]: any } = {}): string {
    const queryParams = getAllQueryParams();
    Object.assign(queryParams, data);
    if (!prefix) prefix = `https://${location.host}${location.pathname}`;
    return prefix + '?' + buildQueryParams(queryParams);
}